import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid,
    Tooltip, Legend, ResponsiveContainer, Cell
} from 'recharts';
import { BarChart2, Users, TrendingUp } from 'lucide-react';
import EmptyState from '../shared/EmptyState';
import KpiTooltip from '../shared/KpiTooltip';

const CHART_TOOLTIP_STYLE = {
    backgroundColor: '#1e293b',
    border: '1px solid #334155',
    borderRadius: '8px',
    color: '#fff',
    fontSize: '12px'
};

const AGENT_COLORS = ['#8b5cf6', '#FF914D', '#22c55e', '#f59e0b', '#ec4899'];

function CustomTooltip({ active, payload, label }) {
    if (!active || !payload) return null;
    return (
        <div className="bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 shadow-xl">
            <p className="text-white text-xs font-semibold mb-1">{label}</p>
            {payload.map((p, i) => (
                <p key={i} className="text-xs" style={{ color: p.color }}>
                    {p.name} : <span className="font-bold">{p.value}</span>
                </p>
            ))}
        </div>
    );
}

export default function AgencyCharts({ chartData }) {
    if (!chartData) return null;

    const hasMonthly = chartData.monthly_activity?.some(m => m.mandates > 0 || m.sales > 0 || m.offers > 0);
    const performers = chartData.top_performers || [];
    const hasPerformers = performers.length > 0;

    // Build comparison data for horizontal bar chart
    const comparisonData = performers.map((p, idx) => ({
        name: p.name?.split(' ')[0] || `Agent ${idx + 1}`,
        fullName: p.name,
        Mandats: p.mandates || 0,
        Ventes: p.sales || 0,
        Note: Math.round((p.rating || 0) * 20), // Convert /5 to /100
        rawRating: (p.rating || 0).toFixed(1),
    }));

    return (
        <div className="space-y-4 sm:space-y-6 mb-4 sm:mb-6">
            {/* Monthly Activity */}
            <Card className="bg-slate-900 border-border">
                <CardHeader className="p-3 sm:p-6 pb-0 sm:pb-0">
                    <CardTitle className="text-white flex items-center gap-2 text-sm sm:text-base">
                        <BarChart2 className="w-4 h-4 sm:w-5 sm:h-5 text-[#FF914D]" />
                        Activité mensuelle
                        <KpiTooltip text="Nombre de mandats créés, vendus et offres reçues chaque mois. Permet de visualiser la dynamique commerciale de l'agence." />
                    </CardTitle>
                </CardHeader>
                <CardContent className="p-2 sm:p-6 pt-2">
                    {hasMonthly ? (
                        <div className="h-[200px] sm:h-[280px]">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={chartData.monthly_activity} barCategoryGap="20%">
                                    <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                                    <XAxis dataKey="month" stroke="#64748b" fontSize={11} tick={{ fill: '#94a3b8' }} />
                                    <YAxis stroke="#64748b" fontSize={11} tick={{ fill: '#94a3b8' }} allowDecimals={false} />
                                    <Tooltip content={<CustomTooltip />} />
                                    <Legend
                                        wrapperStyle={{ fontSize: '11px', color: '#94a3b8' }}
                                        iconType="circle"
                                        iconSize={8}
                                    />
                                    <Bar dataKey="mandates" name="Mandats créés" fill="#8b5cf6" radius={[4, 4, 0, 0]} maxBarSize={32} />
                                    <Bar dataKey="sales" name="Ventes" fill="#22c55e" radius={[4, 4, 0, 0]} maxBarSize={32} />
                                    <Bar dataKey="offers" name="Offres" fill="#FF914D" radius={[4, 4, 0, 0]} maxBarSize={32} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    ) : (
                        <EmptyState
                            icon={<BarChart2 />}
                            title="Pas encore de données"
                            description="L'activité mensuelle apparaîtra dès que des mandats seront créés."
                            compact
                            testId="monthly-chart-empty"
                        />
                    )}
                </CardContent>
            </Card>

            {/* Agent Comparison */}
            {hasPerformers && (
                <Card className="bg-slate-900 border-border">
                    <CardHeader className="p-3 sm:p-6 pb-0 sm:pb-0">
                        <CardTitle className="text-white flex items-center gap-2 text-sm sm:text-base">
                            <Users className="w-4 h-4 sm:w-5 sm:h-5 text-[#FF914D]" />
                            Comparaison agents
                            <KpiTooltip text="Compare vos agents sur 3 axes : nombre de mandats, nombre de ventes et note client (/5 convertie en %). Permet d'identifier les profils commerciaux de chaque agent." />
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="p-2 sm:p-6 pt-3">
                        {/* Visual comparison bars */}
                        <div className="space-y-4">
                            {comparisonData.map((agent, idx) => {
                                const color = AGENT_COLORS[idx % AGENT_COLORS.length];
                                const maxMandats = Math.max(...comparisonData.map(a => a.Mandats), 1);
                                const maxVentes = Math.max(...comparisonData.map(a => a.Ventes), 1);
                                return (
                                    <div key={agent.fullName} className="p-3 rounded-xl bg-white/[0.03] border border-border/50" data-testid={`agent-comparison-${idx}`}>
                                        <div className="flex items-center gap-2.5 mb-3">
                                            <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold" style={{ backgroundColor: color + '33', color }}>
                                                {agent.fullName?.split(' ').map(n => n[0]).join('')}
                                            </div>
                                            <span className="text-sm font-semibold text-foreground">{agent.fullName}</span>
                                            <div className="ml-auto flex items-center gap-1 text-yellow-400">
                                                <span className="text-xs font-bold">{agent.rawRating}/5</span>
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-2 gap-3">
                                            <div>
                                                <div className="flex items-center justify-between text-xs mb-1">
                                                    <span className="text-muted-foreground">Mandats</span>
                                                    <span className="font-bold text-foreground">{agent.Mandats}</span>
                                                </div>
                                                <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                                                    <div className="h-full rounded-full transition-all duration-700" style={{ width: `${(agent.Mandats / maxMandats) * 100}%`, backgroundColor: color }} />
                                                </div>
                                            </div>
                                            <div>
                                                <div className="flex items-center justify-between text-xs mb-1">
                                                    <span className="text-muted-foreground">Ventes</span>
                                                    <span className="font-bold text-green-400">{agent.Ventes}</span>
                                                </div>
                                                <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                                                    <div className="h-full bg-green-500 rounded-full transition-all duration-700" style={{ width: `${(agent.Ventes / Math.max(maxVentes, 1)) * 100}%` }} />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Summary insight */}
                        {comparisonData.length >= 2 && (() => {
                            const best = [...comparisonData].sort((a, b) => b.Mandats - a.Mandats)[0];
                            const bestSeller = [...comparisonData].sort((a, b) => b.Ventes - a.Ventes)[0];
                            return (
                                <div className="mt-4 p-3 rounded-xl bg-gradient-to-r from-[#681793]/10 to-[#FF914D]/5 border border-[#681793]/20">
                                    <p className="text-xs text-muted-foreground">
                                        <span className="text-[#FF914D] font-semibold">{best.fullName}</span> a le plus de mandats ({best.Mandats}).
                                        {bestSeller.fullName !== best.fullName && (
                                            <> <span className="text-green-400 font-semibold">{bestSeller.fullName}</span> a le meilleur taux de vente ({bestSeller.Ventes} ventes).</>
                                        )}
                                    </p>
                                </div>
                            );
                        })()}
                    </CardContent>
                </Card>
            )}

            {/* Monthly Recap Bar Chart */}
            {hasMonthly && performers.length > 1 && (
                <Card className="bg-slate-900 border-border">
                    <CardHeader className="p-3 sm:p-6 pb-0 sm:pb-0">
                        <CardTitle className="text-white flex items-center gap-2 text-sm sm:text-base">
                            <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-green-400" />
                            Répartition des mandats par agent
                            <KpiTooltip text="Nombre total de mandats gérés par chaque agent. Identifie la charge de travail et l'activité commerciale de chacun." />
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="p-2 sm:p-6 pt-2">
                        <div className="h-[200px] sm:h-[250px]">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={comparisonData} layout="vertical" barSize={20} style={{ backgroundColor: 'transparent' }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" horizontal={false} />
                                    <XAxis type="number" stroke="#64748b" fontSize={11} tick={{ fill: '#94a3b8' }} allowDecimals={false} />
                                    <YAxis type="category" dataKey="name" stroke="#64748b" fontSize={11} tick={{ fill: '#e2e8f0' }} width={80} />
                                    <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(255,255,255,0.03)' }} />
                                    <Bar dataKey="Mandats" name="Mandats" radius={[0, 4, 4, 0]}>
                                        {comparisonData.map((_, idx) => (
                                            <Cell key={idx} fill={AGENT_COLORS[idx % AGENT_COLORS.length]} />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </CardContent>
                </Card>
            )}
        </div>
    );
}
